<!--
  - SPDX-FileCopyrightText: 2019 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<NcAppNavigation ref="leftSidebar" :aria-label="t('spreed', 'Conversation list')">
		<template #search>
			<div class="navigation-top">
				<div class="navigation-buttons-container">
					<div class="new-conversation">
						<div
							class="conversations-search"
							:class="{ 'conversations-search--expanded': isSearching }">
							<SearchBox
								ref="searchBox"
								v-model:value="searchText"
								v-model:isFocused="isFocused"
								:listRef="[scroller, searchResults]"
								@input="debounceFetchSearchResults"
								@abortSearch="abortSearch" />
						</div>

						<TransitionWrapper name="radial-reveal">
							<!-- Filters -->
							<NcActions
								v-show="searchText === ''"
								:variant="isFiltered ? 'secondary' : 'tertiary'"
								class="filters"
								:class="{ 'hidden-visually': isSearching }">
								<template #icon>
									<IconFilterVariant :size="20" />
								</template>
								<NcActionCaption :name="t('spreed', 'Filter conversations by')" />

								<NcActionButton
									closeAfterClick
									type="checkbox"
									:modelValue="filters.includes('mentions')"
									@click="handleFilter('mentions')">
									<template #icon>
										<IconAt :size="20" />
									</template>
									{{ t('spreed', 'Unread mentions') }}
								</NcActionButton>

								<NcActionButton
									closeAfterClick
									type="checkbox"
									:modelValue="filters.includes('unread')"
									@click="handleFilter('unread')">
									<template #icon>
										<IconMessageBadgeOutline :size="20" />
									</template>
									{{ t('spreed', 'Unread messages') }}
								</NcActionButton>

								<NcActionButton
									closeAfterClick
									type="checkbox"
									:modelValue="filters.includes('events')"
									@click="handleFilter('events')">
									<template #icon>
										<IconCalendarBlankOutline :size="20" />
									</template>
									{{ t('spreed', 'Meeting conversations') }}
								</NcActionButton>

								<NcActionButton
									v-if="isFiltered"
									closeAfterClick
									class="filter-actions__clearbutton"
									@click="handleFilter(null)">
									<template #icon>
										<IconFilterVariantRemove :size="20" />
									</template>
									{{ t('spreed', 'Clear filters') }}
								</NcActionButton>

								<template v-if="supportSortOrder">
									<NcActionSeparator />

									<NcActionCaption :name="SORT_LABELS.SORTBY_HEADER" />

									<NcActionButton
										closeAfterClick
										type="radio"
										:value="CONVERSATION.SORT_ORDER.ACTIVITY"
										:modelValue="sortOrder"
										@update:modelValue="handleSortOrder">
										<template #icon>
											<IconClockOutline :size="20" />
										</template>
										{{ SORT_LABELS.SORTBY_ACTIVITY }}
									</NcActionButton>

									<NcActionButton
										closeAfterClick
										type="radio"
										:value="CONVERSATION.SORT_ORDER.ALPHABETICAL"
										:modelValue="sortOrder"
										@update:modelValue="handleSortOrder">
										<template #icon>
											<IconSortAlphabeticalAscending :size="20" />
										</template>
										{{ SORT_LABELS.SORTBY_ALPHABET }}
									</NcActionButton>

									<NcActionSeparator />

									<NcActionButton
										closeAfterClick
										type="radio"
										:value="CONVERSATION.GROUP_MODE.NONE"
										:modelValue="groupMode"
										@update:modelValue="handleGroupMode">
										<template #icon>
											<IconFormatListBulleted :size="20" />
										</template>
										{{ SORT_LABELS.GROUPBY_NONE }}
									</NcActionButton>

									<NcActionButton
										closeAfterClick
										type="radio"
										:value="CONVERSATION.GROUP_MODE.PRIVATE_FIRST"
										:modelValue="groupMode"
										@update:modelValue="handleGroupMode">
										<template #icon>
											<IconAccountOutline :size="20" />
										</template>
										{{ SORT_LABELS.GROUPBY_PRIVATE_FIRST }}
									</NcActionButton>

									<NcActionButton
										closeAfterClick
										type="radio"
										:value="CONVERSATION.GROUP_MODE.GROUP_FIRST"
										:modelValue="groupMode"
										@update:modelValue="handleGroupMode">
										<template #icon>
											<IconAccountMultipleOutline :size="20" />
										</template>
										{{ SORT_LABELS.GROUPBY_GROUP_FIRST }}
									</NcActionButton>
								</template>
							</NcActions>
						</TransitionWrapper>

						<!-- Actions -->
						<TransitionWrapper name="radial-reveal">
							<NcActions
								v-show="searchText === ''"
								class="actions"
								:class="{ 'hidden-visually': isSearching }">
								<template #icon>
									<IconChatPlusOutline :size="20" />
								</template>
								<NcActionButton
									v-if="canStartConversations"
									closeAfterClick
									@click="showModalNewConversation">
									<template #icon>
										<IconPlus :size="20" />
									</template>
									{{ t('spreed', 'Create a new conversation') }}
								</NcActionButton>

								<NcActionButton
									v-if="canNoteToSelf && !hasNoteToSelf"
									closeAfterClick
									@click="restoreNoteToSelfConversation">
									<template #icon>
										<IconNoteEditOutline :size="20" />
									</template>
									{{ t('spreed', 'New personal note') }}
								</NcActionButton>

								<NcActionButton
									closeAfterClick
									@click="showModalListConversations">
									<template #icon>
										<IconFormatListBulleted :size="20" />
									</template>
									{{ t('spreed', 'Join open conversations') }}
								</NcActionButton>

								<NcActionButton
									v-if="canModerateSipDialOut"
									closeAfterClick
									@click="showModalCallPhoneDialog">
									<template #icon>
										<IconPhoneOutline :size="20" />
									</template>
									{{ t('spreed', 'Call a phone number') }}
								</NcActionButton>
								<NcActionButton
									v-else-if="hintSipDialOut"
									disabled
									:description="t('spreed', 'SIP backend is not installed')">
									<template #icon>
										<IconPhoneOutline :size="20" />
									</template>
									{{ t('spreed', 'Call a phone number') }}
								</NcActionButton>
							</NcActions>
						</TransitionWrapper>

						<!-- All open conversations list -->
						<OpenConversationsList ref="openConversationsList" />

						<!-- New Conversation dialog -->
						<NewConversationDialog ref="newConversationDialog" :canModerateSipDialOut="canModerateSipDialOut" />

						<!-- New phone (SIP dial-out) dialog -->
						<CallPhoneDialog v-if="canModerateSipDialOut" ref="callPhoneDialog" />

						<!-- New Pending Invitations dialog -->
						<InvitationHandler v-if="pendingInvitationsCount" ref="invitationHandler" />
					</div>
					<TransitionWrapper
						v-if="filters.length"
						class="conversations__filters"
						name="zoom"
						tag="div"
						group>
						<NcChip
							v-for="filter in filters"
							:key="filter"
							:text="FILTER_LABELS[filter]"
							@close="handleFilter(filter)" />
					</TransitionWrapper>
				</div>

				<div v-if="!isSearching" class="navigation-buttons-container">
					<LeftSidebarButton
						:to="{ name: 'root' }"
						:active="$route.name === 'root'"
						@click="refreshTalkDashboard">
						<template #icon>
							<IconHomeOutline :size="20" />
						</template>
						{{ HOME_BUTTON_LABEL }}
					</LeftSidebarButton>
					<template v-if="showArchived || showThreadsList">
						<LeftSidebarButton @click="handleBackToConversations">
							<template #icon>
								<IconArrowLeft class="bidirectional-icon" :size="20" />
							</template>
							{{ t('spreed', 'Back to conversations') }}
						</LeftSidebarButton>
					</template>
					<LeftSidebarButton
						v-else-if="supportThreads && !showThreadsList && !isSearching && !isFiltered"
						@click="handleShowThreadsList">
						<template #icon>
							<IconForumOutline :size="20" />
						</template>
						{{ t('spreed', 'Threads') }}
					</LeftSidebarButton>
					<LeftSidebarButton
						v-if="pendingInvitationsCount && !isSearching && !showArchived && !showThreadsList"
						@click="showInvitationHandler">
						<template #icon>
							<IconAccountMultiplePlusOutline :size="20" />
						</template>
						{{ t('spreed', 'Pending invitations') }}
						<template #badge>
							<NcCounterBubble type="highlighted" :count="pendingInvitationsCount" />
						</template>
					</LeftSidebarButton>
				</div>

				<div v-if="!isSearching" class="navigation-buttons-container" :class="{ 'hidden-visually': !showArchived && !showThreadsList }">
					<div
						:id="LIST_HEADING_ID"
						class="navigation-caption"
						role="heading"
						aria-level="3">
						{{
							showArchived && t('spreed', 'Archived conversations')
								|| showThreadsList && t('spreed', 'Threads')
								|| t('spreed', 'Conversations')
						}}
					</div>
				</div>
			</div>
		</template>

		<template #list>
			<!-- Conversations List -->
			<template v-if="!isSearching">
				<NcEmptyContent
					v-if="showEmptyContent"
					:name="emptyContentLabel"
					:description="emptyContentDescription">
					<template #icon>
						<IconAt v-if="filters.length === 1 && filters[0] === 'mentions'" :size="64" />
						<IconMessageBadgeOutline v-else-if="filters.length === 1 && filters[0] === 'unread'" :size="64" />
						<IconArchiveOutline v-else-if="showArchived" :size="64" />
						<IconForumOutline v-else-if="showThreadsList" :size="64" />
						<IconMessageOutline v-else :size="64" />
					</template>
					<template #action>
						<NcButton v-if="isFiltered" @click="handleFilter(null)">
							<template #icon>
								<IconFilterVariantRemove :size="20" />
							</template>
							{{ t('spreed', 'Clear filter') }}
						</NcButton>
					</template>
				</NcEmptyContent>
				<template v-if="showThreadsList">
					<LoadingPlaceholder v-if="!followedThreadsInitialised" type="conversations" />
					<ul v-else class="threads-tab__list" :aria-labelledby="LIST_HEADING_ID">
						<ThreadItem
							v-for="thread of followedThreads"
							:key="`thread_${thread.thread.id}`"
							:thread="thread" />
					</ul>
					<div v-if="!allFollowedThreadsReceived" class="navigation-buttons-container">
						<LeftSidebarButton
							:compact="false /* ThreadItem-s are never compact */"
							@click="loadMoreFollowedThreads">
							<template #icon>
								<IconForumOutline :size="20" />
							</template>
							{{ t('spreed', 'Show more threads') }}
						</LeftSidebarButton>
					</div>
				</template>
				<ConversationsListVirtual
					v-else
					v-show="sortedConversationsList.length > 0"
					ref="scroller"
					:token="token"
					:listAriaLabelledBy="LIST_HEADING_ID"
					:conversations="sortedConversationsList"
					:loading="!conversationsInitialised"
					:compact="isCompact"
					:showTags="supportTags && !showArchived"
					class="scroller"
					@scroll="debounceHandleScroll" />
				<NcButton
					v-if="!showThreadsList && lastUnreadMentionBelowViewportIndex !== null && !filters.includes('mentions')"
					class="unread-mention-button"
					variant="primary"
					@click="scrollBottomUnread">
					{{ t('spreed', 'Unread mentions') }}
				</NcButton>
			</template>

			<!-- Search results -->
			<SearchConversationsResults
				v-else
				ref="searchResults"
				class="scroller"
				:searchText="searchText"
				:searchResultsLoading="searchResultsLoading"
				:conversationsList="conversationsList"
				:searchResults="searchResults"
				:searchResultsListedConversations="searchResultsListedConversations"
				@abortSearch="abortSearch"
				@createNewConversation="createConversation"
				@createAndJoinConversation="createAndJoinConversation" />
		</template>

		<template #footer>
			<div class="navigation-bottom">
				<div class="navigation-buttons-container">
					<LeftSidebarButton
						v-if="supportsArchive && !isSearching && !showArchived && !showThreadsList && archivedConversationsList.length"
						@click="showArchived = true; showThreadsList = false">
						<template #icon>
							<IconArchiveOutline :size="20" />
						</template>
						{{ t('spreed', 'Archived conversations') }}
						<template #badge>
							<span v-if="showArchivedConversationsBubble" class="archived-conversations-bubble">
								⬤
							</span>
						</template>
					</LeftSidebarButton>

					<LeftSidebarButton
						@click="showSettings">
						<template #icon>
							<IconCogOutline :size="20" />
						</template>
						{{ t('spreed', 'App settings') }}
					</LeftSidebarButton>
				</div>
			</div>
		</template>
	</NcAppNavigation>
</template>

<script>
import { isCancel } from '@nextcloud/axios'
import { showError } from '@nextcloud/dialogs'
import { emit } from '@nextcloud/event-bus'
import { t } from '@nextcloud/l10n'
import { useIsMobile } from '@nextcloud/vue/composables/useIsMobile'
import debounce from 'debounce'
import { ref } from 'vue'
import { START_LOCATION } from 'vue-router'
import NcActionButton from '@nextcloud/vue/components/NcActionButton'
import NcActionCaption from '@nextcloud/vue/components/NcActionCaption'
import NcActions from '@nextcloud/vue/components/NcActions'
import NcActionSeparator from '@nextcloud/vue/components/NcActionSeparator'
import NcAppNavigation from '@nextcloud/vue/components/NcAppNavigation'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcChip from '@nextcloud/vue/components/NcChip'
import NcCounterBubble from '@nextcloud/vue/components/NcCounterBubble'
import NcEmptyContent from '@nextcloud/vue/components/NcEmptyContent'
import IconAccountMultipleOutline from 'vue-material-design-icons/AccountMultipleOutline.vue'
import IconAccountMultiplePlusOutline from 'vue-material-design-icons/AccountMultiplePlusOutline.vue'
import IconAccountOutline from 'vue-material-design-icons/AccountOutline.vue'
import IconArchiveOutline from 'vue-material-design-icons/ArchiveOutline.vue'
import IconArrowLeft from 'vue-material-design-icons/ArrowLeft.vue'
import IconAt from 'vue-material-design-icons/At.vue'
import IconCalendarBlankOutline from 'vue-material-design-icons/CalendarBlankOutline.vue'
import IconChatPlusOutline from 'vue-material-design-icons/ChatPlusOutline.vue'
import IconClockOutline from 'vue-material-design-icons/ClockOutline.vue'
import IconCogOutline from 'vue-material-design-icons/CogOutline.vue'
import IconFilterVariant from 'vue-material-design-icons/FilterVariant.vue'
import IconFilterVariantRemove from 'vue-material-design-icons/FilterVariantRemove.vue'
import IconFormatListBulleted from 'vue-material-design-icons/FormatListBulleted.vue'
import IconForumOutline from 'vue-material-design-icons/ForumOutline.vue'
import IconHomeOutline from 'vue-material-design-icons/HomeOutline.vue'
import IconMessageBadgeOutline from 'vue-material-design-icons/MessageBadgeOutline.vue'
import IconMessageOutline from 'vue-material-design-icons/MessageOutline.vue'
import IconNoteEditOutline from 'vue-material-design-icons/NoteEditOutline.vue'
import IconPhoneOutline from 'vue-material-design-icons/PhoneOutline.vue'
import IconPlus from 'vue-material-design-icons/Plus.vue'
import IconSortAlphabeticalAscending from 'vue-material-design-icons/SortAlphabeticalAscending.vue'
import NewConversationDialog from '../NewConversationDialog/NewConversationDialog.vue'
import ThreadItem from '../RightSidebar/Threads/ThreadItem.vue'
import LoadingPlaceholder from '../UIShared/LoadingPlaceholder.vue'
import SearchBox from '../UIShared/SearchBox.vue'
import TransitionWrapper from '../UIShared/TransitionWrapper.vue'
import CallPhoneDialog from './CallPhoneDialog/CallPhoneDialog.vue'
import ConversationsListVirtual from './ConversationsList/ConversationsListVirtual.vue'
import InvitationHandler from './InvitationHandler.vue'
import LeftSidebarButton from './LeftSidebarButton.vue'
import OpenConversationsList from './OpenConversationsList/OpenConversationsList.vue'
import SearchConversationsResults from './SearchConversationsResults/SearchConversationsResults.vue'
import { useArrowNavigation } from '../../composables/useArrowNavigation.js'
import { useGetToken } from '../../composables/useGetToken.ts'
import { ATTENDEE, CONVERSATION } from '../../constants.ts'
import BrowserStorage from '../../services/BrowserStorage.js'
import {
	getTalkConfig,
	hasTalkFeature,
	showTalkFeatureHint,
} from '../../services/CapabilitiesManager.ts'
import {
	createLegacyConversation,
	fetchNoteToSelfConversation,
	searchListedConversations,
} from '../../services/conversationsService.ts'
import { autocompleteQuery } from '../../services/coreService.ts'
import { EventBus } from '../../services/EventBus.ts'
import { talkBroadcastChannel } from '../../services/talkBroadcastChannel.js'
import { useActorStore } from '../../stores/actor.ts'
import { useChatExtrasStore } from '../../stores/chatExtras.ts'
import { useConversationTagsStore } from '../../stores/conversationTags.ts'
import { useFederationStore } from '../../stores/federation.ts'
import { useSettingsStore } from '../../stores/settings.ts'
import { useTalkHashStore } from '../../stores/talkHash.js'
import { useTokenStore } from '../../stores/token.ts'
import CancelableRequest from '../../utils/CancelableRequest.ts'
import {
	filterConversation,
	hasCall,
	hasUnreadMentions,
	shouldIncludeArchived,
	sortConversationsList,
} from '../../utils/conversation.ts'
import { requestTabLeadership } from '../../utils/requestTabLeadership.js'

const isFederationEnabled = getTalkConfig('local', 'federation', 'enabled')
const canModerateSipDialOut = hasTalkFeature('local', 'sip-support-dialout')
	&& getTalkConfig('local', 'call', 'sip-enabled')
	&& getTalkConfig('local', 'call', 'sip-dialout-enabled')
	&& getTalkConfig('local', 'call', 'can-enable-sip')
const hintSipDialOut = !canModerateSipDialOut && showTalkFeatureHint(34)
const canNoteToSelf = hasTalkFeature('local', 'note-to-self')
const supportsArchive = hasTalkFeature('local', 'archived-conversations-v2')
const supportThreads = hasTalkFeature('local', 'threads')
const supportSortOrder = getTalkConfig('local', 'conversations', 'sort-order') !== undefined
const supportTags = hasTalkFeature('local', 'conversation-tags')

// TRANSLATORS The main home view
const HOME_BUTTON_LABEL = t('spreed', 'Home')
const FILTER_LABELS = {
	unread: t('spreed', 'Unread'),
	mentions: t('spreed', 'Mentions'),
	events: t('spreed', 'Meetings'),
	default: '',
}
const SORT_LABELS = {
	// TRANSLATORS Navigation actions: sort conversations by recent activity / sort alphabetically
	SORTBY_HEADER: t('spreed', 'Sort conversations'),
	// TRANSLATORS Navigation actions: sort conversations by recent activity / sort alphabetically
	SORTBY_ACTIVITY: t('spreed', 'By recent activity'),
	// TRANSLATORS Navigation actions: sort conversations by recent activity / sort alphabetically
	SORTBY_ALPHABET: t('spreed', 'Alphabetically'),

	// TRANSLATORS Navigation actions: arrange list by putting private or group conversations on top
	GROUPBY_NONE: t('spreed', 'Do not group by type'),
	// TRANSLATORS Navigation actions: arrange list by putting private or group conversations on top
	GROUPBY_PRIVATE_FIRST: t('spreed', 'Show private ones first'),
	// TRANSLATORS Navigation actions: arrange list by putting private or group conversations on top
	GROUPBY_GROUP_FIRST: t('spreed', 'Show group ones first'),
}

let actualizeDataTimeout = null

export default {
	name: 'LeftSidebar',

	components: {
		LoadingPlaceholder,
		ThreadItem,
		CallPhoneDialog,
		InvitationHandler,
		NcAppNavigation,
		NcButton,
		NcCounterBubble,
		NcChip,
		SearchBox,
		NewConversationDialog,
		OpenConversationsList,
		NcActions,
		NcActionButton,
		NcActionCaption,
		NcActionSeparator,
		NcEmptyContent,
		TransitionWrapper,
		ConversationsListVirtual,
		SearchConversationsResults,
		LeftSidebarButton,
		// Icons
		IconAccountMultiplePlusOutline,
		IconAt,
		IconMessageBadgeOutline,
		IconMessageOutline,
		IconFilterVariant,
		IconFilterVariantRemove,
		IconArchiveOutline,
		IconArrowLeft,
		IconCalendarBlankOutline,
		IconForumOutline,
		IconHomeOutline,
		IconPhoneOutline,
		IconPlus,
		IconChatPlusOutline,
		IconCogOutline,
		IconFormatListBulleted,
		IconNoteEditOutline,
		IconSortAlphabeticalAscending,
		IconClockOutline,
		IconAccountMultipleOutline,
		IconAccountOutline,
	},

	setup() {
		const leftSidebar = ref(null)
		const searchBox = ref(null)
		const scroller = ref(null)

		const showArchived = ref(false)
		const showThreadsList = ref(false)
		const filters = ref(BrowserStorage.getItem('filterEnabled')?.split(',') ?? [])

		const federationStore = useFederationStore()
		const talkHashStore = useTalkHashStore()
		const settingsStore = useSettingsStore()
		const tagsStore = useConversationTagsStore()
		const { initializeNavigation, resetNavigation } = useArrowNavigation(leftSidebar, searchBox)
		const isMobile = useIsMobile()

		const LIST_HEADING_ID = 'left-sidebar-list-heading'

		return {
			token: useGetToken(),
			initializeNavigation,
			resetNavigation,
			leftSidebar,
			filters,
			searchBox,
			scroller,
			federationStore,
			talkHashStore,
			isMobile,
			canModerateSipDialOut,
			hintSipDialOut,
			canNoteToSelf,
			supportsArchive,
			supportThreads,
			supportSortOrder,
			supportTags,
			showArchived,
			showThreadsList,
			settingsStore,
			CONVERSATION,
			HOME_BUTTON_LABEL,
			FILTER_LABELS,
			SORT_LABELS,
			LIST_HEADING_ID,
			actorStore: useActorStore(),
			chatExtrasStore: useChatExtrasStore(),
			tokenStore: useTokenStore(),
			tagsStore,
		}
	},

	data() {
		return {
			searchText: '',
			searchResults: [],
			searchResultsListedConversations: [],
			searchResultsLoading: true,
			canStartConversations: getTalkConfig('local', 'conversations', 'can-create'),
			cancelSearchPossibleConversations: () => {},
			cancelSearchListedConversations: () => {},
			debounceFetchSearchResults: () => {},
			debounceFetchConversations: () => {},
			debounceHandleScroll: () => {},
			refreshTimer: null,
			/**
			 * @type {number|null}
			 */
			lastUnreadMentionBelowViewportIndex: null,
			roomListModifiedBefore: 0,
			forceFullRoomListRefreshAfterXLoops: 0,
			isFetchingConversations: false,
			isCurrentTabLeader: false,
			isFocused: false,
			isNavigating: false,
			fallbackConversationToken: null,
		}
	},

	computed: {
		sortOrder() {
			return this.settingsStore.sortOrder
		},

		groupMode() {
			return this.settingsStore.groupMode
		},

		conversationsList() {
			return this.$store.getters.conversationsList
		},

		sortedConversationsList() {
			return sortConversationsList(this.filteredConversationsList, this.groupMode, this.sortOrder)
		},

		unreadMentionIndices() {
			const indices = []
			for (const i in this.sortedConversationsList) {
				if (hasUnreadMentions(this.sortedConversationsList[i])) {
					indices.push(i)
				}
			}
			return indices
		},

		emptyContentLabel() {
			if (this.showThreadsList) {
				return t('spreed', 'No followed threads')
			} else if (this.isFiltered) {
				return t('spreed', 'No matches found')
			} else {
				return t('spreed', 'No conversations found')
			}
		},

		emptyContentDescription() {
			if (this.showArchived) {
				return t('spreed', 'You have no archived conversations.')
			} else if (this.showThreadsList) {
				return t('spreed', 'Subscribe to an existing thread or start your own.')
			}
			if (this.filters.length === 1 && this.filters[0] === 'mentions') {
				return t('spreed', 'You have no unread mentions.')
			} else if (this.filters.length === 1 && this.filters[0] === 'unread') {
				return t('spreed', 'You have no unread messages.')
			} else {
				return ''
			}
		},

		archivedConversationsList() {
			return this.$store.getters.archivedConversationsList
		},

		showArchivedConversationsBubble() {
			return this.archivedConversationsList
				.some((conversation) => hasUnreadMentions(conversation) || hasCall(conversation))
		},

		filteredConversationsList() {
			if (this.isFocused) {
				return this.conversationsList.filter((conversation) => shouldIncludeArchived(conversation, this.showArchived))
			}

			let validConversationsCount = 0
			const filteredConversations = this.conversationsList.filter((conversation) => {
				const conversationIsValid = filterConversation(conversation, this.filters)
				if (conversationIsValid) {
					validConversationsCount++
				}
				return shouldIncludeArchived(conversation, this.showArchived)
					&& (conversationIsValid || hasCall(conversation) || conversation.token === this.token)
			})
			// return empty if it only includes the current conversation without any flags
			return validConversationsCount === 0 && !this.isNavigating ? [] : filteredConversations
		},

		followedThreads() {
			return this.chatExtrasStore.followedThreadsList
		},

		followedThreadsInitialised() {
			return this.chatExtrasStore.followedThreadsInitialised
		},

		allFollowedThreadsReceived() {
			return this.chatExtrasStore.allFollowedThreadsReceived
		},

		isSearching() {
			return this.searchText !== ''
		},

		hasNoteToSelf() {
			return this.conversationsList.find((conversation) => conversation.type === CONVERSATION.TYPE.NOTE_TO_SELF)
		},

		pendingInvitationsCount() {
			return isFederationEnabled
				? this.federationStore.pendingSharesCount
				: 0
		},

		isCompact() {
			return this.settingsStore.conversationsListStyle === CONVERSATION.LIST_STYLE.COMPACT
		},

		isFiltered() {
			return this.filters.length !== 0
		},

		conversationsInitialised() {
			return this.$store.getters.conversationsInitialised
		},

		showEmptyContent() {
			return (this.conversationsInitialised && !this.sortedConversationsList.length)
				|| (this.showThreadsList && this.followedThreadsInitialised && !this.followedThreads.length)
		},
	},

	watch: {
		token(value) {
			if (value && this.isFiltered) {
				this.isNavigating = true
			}
		},

		showThreadsList(value) {
			if (value) {
				// Refresh a list
				this.chatExtrasStore.fetchFollowedThreadsList()
			}
		},

		unreadMentionIndices() {
			this.debounceHandleScroll()
		},
	},

	beforeMount() {
		// Restore last fetched conversations from browser storage,
		// before updated ones come from server
		this.restoreConversations()

		requestTabLeadership().then(() => {
			this.isCurrentTabLeader = true
			this.fetchConversations()
			// Refreshes the conversations list every 30 seconds
			this.refreshTimer = window.setInterval(() => {
				this.fetchConversations()
			}, 30000)
		})

		talkBroadcastChannel.addEventListener('message', (event) => {
			if (this.isCurrentTabLeader) {
				switch (event.data.message) {
					case 'force-fetch-all-conversations':
						if (event.data.options?.all) {
							this.roomListModifiedBefore = 0
							this.forceFullRoomListRefreshAfterXLoops = 10
						}
						this.debounceFetchConversations()
						break
				}
			} else {
				switch (event.data.message) {
					case 'update-conversations':
						this.$store.dispatch('patchConversations', {
							conversations: event.data.conversations,
							withRemoving: event.data.withRemoving,
						})
						this.federationStore.updatePendingSharesCount(event.data.invites)
						break
					case 'update-nextcloud-talk-hash':
						this.talkHashStore.setNextcloudTalkHash(event.data.hash)
						break
				}
			}
		})
	},

	mounted() {
		this.debounceFetchSearchResults = debounce(this.fetchSearchResults, 250)
		this.debounceFetchConversations = debounce(this.fetchConversations, 3000)
		this.debounceHandleScroll = debounce(this.handleScroll, 100)

		if (supportTags) {
			// Fetch conversation tags
			this.tagsStore.fetchTags()
		}

		EventBus.on('should-refresh-conversations', this.handleShouldRefreshConversations)
		EventBus.once('conversations-received', this.handleConversationsReceived)
		EventBus.on('route-change', this.onRouteChange)
		EventBus.on('new-conversation-dialog:show', this.showModalNewConversation)
		EventBus.on('open-conversations-list:show', this.showModalListConversations)
		EventBus.on('call-phone-dialog:show', this.showModalCallPhoneDialog)
	},

	beforeUnmount() {
		this.debounceFetchSearchResults.clear?.()
		this.debounceFetchConversations.clear?.()
		this.debounceHandleScroll.clear?.()

		EventBus.off('should-refresh-conversations', this.handleShouldRefreshConversations)
		EventBus.off('conversations-received', this.handleConversationsReceived)
		EventBus.off('route-change', this.onRouteChange)
		EventBus.off('new-conversation-dialog:show', this.showModalNewConversation)
		EventBus.off('open-conversations-list:show', this.showModalListConversations)
		EventBus.off('call-phone-dialog:show', this.showModalCallPhoneDialog)

		this.cancelSearchPossibleConversations()
		this.cancelSearchPossibleConversations = null

		this.cancelSearchListedConversations()
		this.cancelSearchListedConversations = null

		if (this.refreshTimer) {
			clearInterval(this.refreshTimer)
			this.refreshTimer = null
		}
	},

	methods: {
		loadMoreFollowedThreads() {
			this.chatExtrasStore.fetchFollowedThreadsList(this.followedThreads.length)
		},

		t,
		showModalNewConversation() {
			this.$refs.newConversationDialog.showModal()
		},

		showModalListConversations() {
			this.$refs.openConversationsList.showModal()
		},

		showModalCallPhoneDialog() {
			this.$refs.callPhoneDialog.showModal()
		},

		showInvitationHandler() {
			this.$refs.invitationHandler.showModal()
		},

		handleSortOrder(newValue) {
			this.settingsStore.updateSortOrder(newValue)
		},

		handleGroupMode(newValue) {
			this.settingsStore.updateGroupMode(newValue)
		},

		handleFilter(filter) {
			// Store the active filter
			if (filter === null) {
				this.filters = []
			} else {
				if (this.filters.includes(filter)) {
					this.filters = this.filters.filter((f) => f !== filter)
				} else {
					// Hardcode 'unread' and 'mentions' to behave like radio buttons
					if (filter === 'unread' || filter === 'mentions') {
						this.filters = [...this.filters.filter((f) => f !== 'unread' && f !== 'mentions'), filter]
					} else {
						this.filters = [...this.filters, filter]
					}
				}
			}

			if (this.filters.length) {
				this.showThreadsList = false
				BrowserStorage.setItem('filterEnabled', this.filters)
			} else {
				BrowserStorage.removeItem('filterEnabled')
			}

			// Clear the search input once a filter is active
			this.searchText = ''
			// Initiate the navigation status
			this.isNavigating = false
		},

		scrollBottomUnread() {
			this.$refs.scroller.scrollToItem(this.lastUnreadMentionBelowViewportIndex)
		},

		async fetchPossibleConversations() {
			try {
				// FIXME: move to conversationsStore
				this.cancelSearchPossibleConversations('canceled')
				const { request, cancel } = CancelableRequest(autocompleteQuery)
				this.cancelSearchPossibleConversations = cancel

				const response = await request({
					searchText: this.searchText,
					token: 'new',
					onlyUsers: !this.canStartConversations,
				})

				const oneToOneMap = this.conversationsList.reduce((acc, result) => {
					if (result.type === CONVERSATION.TYPE.ONE_TO_ONE) {
						acc.push(result.name)
					}
					return acc
				}, [this.actorStore.userId])

				this.searchResults = response?.data?.ocs?.data.filter((match) => {
					return !(match.source === ATTENDEE.ACTOR_TYPE.USERS && oneToOneMap.includes(match.id))
				}) ?? []
			} catch (exception) {
				if (isCancel(exception)) {
					return
				}
				console.error('Error searching for possible conversations', exception)
				showError(t('spreed', 'An error occurred while performing the search'))
			}
		},

		async fetchListedConversations() {
			try {
				// FIXME: move to conversationsStore
				this.cancelSearchListedConversations('canceled')
				const { request, cancel } = CancelableRequest(searchListedConversations)
				this.cancelSearchListedConversations = cancel

				const response = await request(this.searchText)
				this.searchResultsListedConversations = response.data.ocs.data
			} catch (exception) {
				if (isCancel(exception)) {
					return
				}
				console.error('Error searching for open conversations', exception)
				showError(t('spreed', 'An error occurred while performing the search'))
			}
		},

		async fetchSearchResults() {
			if (!this.isSearching) {
				return
			}

			// Reset modes
			this.showArchived = false
			this.showThreadsList = false

			this.resetNavigation()
			this.searchResultsLoading = true
			await Promise.all([this.fetchPossibleConversations(), this.fetchListedConversations()])
			this.searchResultsLoading = false
			this.initializeNavigation()
		},

		/**
		 * Create a new conversation with the selected user
		 * or bring up the dialog to create a new group/circle conversation
		 *
		 * @param {object} item The autocomplete suggestion to start a conversation with
		 * @param {string} item.id The ID of the target
		 * @param {string} item.label The displayname of the target
		 * @param {string} item.source The source of the target (e.g. users, groups, circle)
		 */
		async createAndJoinConversation(item) {
			if (item.source === ATTENDEE.ACTOR_TYPE.USERS) {
				// Create one-to-one conversation directly
				const conversation = await this.$store.dispatch('createOneToOneConversation', item.id)
				this.abortSearch()
				this.$router.push({
					name: 'conversation',
					params: { token: conversation.token },
				}).catch((err) => console.debug(`Error while pushing the new conversation's route: ${err}`))
			} else {
				// For other types, show the modal directly
				this.$refs.newConversationDialog.showModalForItem(item)
			}
		},

		switchToConversation(conversation) {
			this.$store.dispatch('addConversation', conversation)
			this.abortSearch()
			this.$router.push({
				name: 'conversation',
				params: { token: conversation.token },
			}).catch((err) => console.debug(`Error while pushing the new conversation's route: ${err}`))
		},

		async createConversation(roomName) {
			try {
				const response = await createLegacyConversation({
					roomType: CONVERSATION.TYPE.GROUP,
					roomName,
				})
				const conversation = response.data.ocs.data
				this.switchToConversation(conversation)
			} catch (error) {
				console.error('Error creating new private conversation: ', error)
			}
		},

		async restoreNoteToSelfConversation() {
			const response = await fetchNoteToSelfConversation()
			const conversation = response.data.ocs.data
			this.switchToConversation(conversation)
		},

		// Reset the search text, therefore end the search operation.
		abortSearch() {
			this.searchText = ''
			this.isFocused = false
			if (this.cancelSearchPossibleConversations) {
				this.cancelSearchPossibleConversations()
			}
			if (this.cancelSearchListedConversations) {
				this.cancelSearchListedConversations()
			}
		},

		showSettings() {
			// FIXME: use local EventBus service instead of the global one
			emit('show-settings')
		},

		/**
		 * @param {object} [options] Options for conversation refreshing
		 * @param {string} [options.token] The conversation token that got update
		 * @param {object} [options.properties] List of changed properties
		 * @param {boolean} [options.all] Whether all conversations should be fetched
		 */
		async handleShouldRefreshConversations(options) {
			if (options?.token && options?.properties) {
				await this.$store.dispatch('setConversationProperties', {
					token: options.token,
					properties: options.properties,
				})
			}

			if (this.isCurrentTabLeader) {
				if (options?.all === true) {
					this.roomListModifiedBefore = 0
					this.forceFullRoomListRefreshAfterXLoops = 10
				}
				this.debounceFetchConversations()
			} else {
				talkBroadcastChannel.postMessage({ message: 'force-fetch-all-conversations', options })
			}
		},

		async fetchConversations() {
			if (this.isFetchingConversations) {
				return
			}

			this.isFetchingConversations = true
			if (this.forceFullRoomListRefreshAfterXLoops === 0) {
				this.roomListModifiedBefore = 0
				this.forceFullRoomListRefreshAfterXLoops = 10
			} else {
				this.forceFullRoomListRefreshAfterXLoops--
			}

			/**
			 * Fetches the conversations from the server and then adds them one by one
			 * to the store.
			 */
			try {
				const response = await this.$store.dispatch('fetchConversations', {
					modifiedSince: this.roomListModifiedBefore,
				})

				// We can only support this with the HPB as otherwise rooms,
				// you are not currently active in, will not be removed anymore,
				// as there is no signaling message about it when the internal
				// signaling is used.
				if (getTalkConfig('local', 'signaling', 'mode') !== 'internal') {
					if (response?.headers && response.headers['x-nextcloud-talk-modified-before']) {
						this.roomListModifiedBefore = response.headers['x-nextcloud-talk-modified-before']
					}
				}

				/**
				 * Emits a global event that is used in App.vue to update the page title once the
				 * ( if the current route is a conversation and once the conversations are received)
				 */
				EventBus.emit('conversations-received', {})
				this.isFetchingConversations = false
			} catch (error) {
				console.debug('Error while fetching conversations: ', error)
				this.isFetchingConversations = false
			}
		},

		async restoreConversations() {
			try {
				if (await this.$store.dispatch('restoreConversations')) {
					EventBus.emit('conversations-received', { fromBrowserStorage: true })
				}
			} catch (error) {
				console.debug('Error while restoring conversations: ', error)
			}
		},

		handleConversationsReceived() {
			if (this.$route.params.token) {
				this.showArchived = this.$store.getters.conversation(this.$route.params.token)?.isArchived ?? false
				this.scrollToConversation(this.$route.params.token)
			}
		},

		handleScroll() {
			this.computeLastUnreadMention()
		},

		/**
		 * Find position of the last unread conversation below viewport.
		 * Iterates only over indices with unread mentions (cached via the
		 * unreadMentionIndices computed) instead of the full list.
		 */
		computeLastUnreadMention() {
			if (!this.$refs.scroller) {
				this.lastUnreadMentionBelowViewportIndex = null
				return
			}
			const lastInViewport = this.$refs.scroller.getLastItemInViewportIndex()
			this.lastUnreadMentionBelowViewportIndex = this.unreadMentionIndices
				.findLast((idx) => idx > lastInViewport) ?? null
		},

		async scrollToConversation(token) {
			await this.$nextTick()

			if (!this.$refs.scroller) {
				return
			}

			this.$refs.scroller.scrollToConversation(token)
		},

		onRouteChange({ from, to }) {
			if (from.name === 'conversation'
				&& to.name === 'conversation'
				&& from.params.token === to.params.token) {
				// this is triggered when the hash in the URL changes
				if (!to.query?.threadId) {
					this.showThreadsList = false
					this.fallbackConversationToken = null
				}
				return
			}
			if (to.name === 'conversation') {
				this.abortSearch()
				this.showArchived = this.$store.getters.conversation(to.params.token)?.isArchived ?? false
				this.scrollToConversation(to.params.token)
			}
			if (this.isMobile) {
				emit('toggle-navigation', {
					open: to.name === 'root' && from === START_LOCATION,
				})
			}
		},

		refreshTalkDashboard(event) {
			// Throttle click and keyboard events
			if (actualizeDataTimeout) {
				return
			}
			actualizeDataTimeout = setTimeout(() => {
				actualizeDataTimeout = null
			}, 5_000)

			// Reset modes
			this.showArchived = false
			this.showThreadsList = false

			if (this.$route.name === 'root') {
				event.preventDefault()
				EventBus.emit('refresh-talk-dashboard')
			}
		},

		handleShowThreadsList() {
			this.showThreadsList = true
			this.showArchived = false
			this.fallbackConversationToken = this.$route.params?.token
		},

		handleBackToConversations() {
			this.showThreadsList = false
			this.showArchived = false
			if (this.fallbackConversationToken) {
				this.$router.push({
					name: 'conversation',
					params: { token: this.fallbackConversationToken },
				}).catch((err) => console.debug(`Error while pushing the fallback conversation's route: ${err}`))
				this.fallbackConversationToken = null
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.scroller {
	height: 100%;
	padding-inline: var(--default-grid-baseline);
	overflow-y: auto;
	line-height: 20px;
}

.navigation-top {
	display: flex;
	flex-direction: column;
	gap: calc(2 * var(--default-grid-baseline));
	padding-block: calc(2 * var(--default-grid-baseline)) var(--default-grid-baseline);
}

.navigation-bottom {
	padding-block: var(--default-grid-baseline) calc(2 * var(--default-grid-baseline));
}

.navigation-buttons-container {
	display: flex;
	flex-direction: column;
	gap: var(--default-grid-baseline);
	// Inline with NcAppNavigation list
	padding-inline: calc(2 * var(--default-grid-baseline));
}

.new-conversation {
	position: relative;
	display: flex;
	align-items: center;

	.filters {
		position: absolute;
		top: 0;
		inset-inline-end: calc(var(--default-grid-baseline) + var(--default-clickable-area));
	}

	.actions {
		position: absolute;
		top: 0;
		inset-inline-end: 0;
	}
}

.navigation-caption {
	display: flex;
	align-items: center;
	padding-inline: var(--default-grid-baseline);
	height: var(--default-clickable-area);
	font-weight: bold;
}

.threads-tab__list {
	padding-inline: var(--default-grid-baseline);
}

.unread-mention-button {
	position: absolute !important;
	/* stylelint-disable-next-line csstools/use-logical */
	left: 50%;
	transform: translateX(-50%);
	z-index: 100;
	bottom: 10px;
	white-space: nowrap;
}

.conversations-search {
	transition: all 0.15s ease;
	z-index: 1;
	// TODO replace with NcAppNavigationSearch
	width: calc(100% - (var(--default-grid-baseline) + var(--default-clickable-area)) * 2);
	display: flex;

	&--expanded {
		width: 100%;
	}

	:deep(.input-field) {
		margin-block-start: 0;
	}
}

.conversations__filters {
	display: flex;
	flex-wrap: wrap;
	gap: var(--default-grid-baseline);
}

.archived-conversations-bubble {
	margin-inline: var(--default-grid-baseline);
	color: var(--color-primary-element);
}

:deep(.empty-content) {
	text-align: center;
	padding: 20% 10px 0;
}

:deep(.app-navigation__list) {
	padding: 0 !important;
}
</style>
